<template>
  <teleport to="body">
    <!-- Backdrop -->
    <transition name="fade">
      <div v-if="modelValue" class="csb-backdrop" @click="$emit('update:modelValue', false)" />
    </transition>

    <!-- Painel -->
    <transition name="slide">
      <div v-if="modelValue" class="csb-panel" role="dialog" aria-modal="true" aria-label="Personalizar página do evento">
        <!-- Header -->
        <div class="csb-header">
          <div class="csb-header__info">
            <i class="fas fa-palette csb-header__icon"></i>
            <span class="csb-header__title">Personalizar</span>
          </div>
          <button class="csb-close" @click="$emit('update:modelValue', false)" aria-label="Fechar">
            <i class="fas fa-xmark"></i>
          </button>
        </div>

        <div class="csb-body">
          <!-- Aviso de visibilidade exclusiva -->
          <div class="csb-owner-notice">
            <i class="fas fa-eye csb-owner-notice__icon"></i>
            <span>Apenas você vê esta opção. Seus convidados não têm acesso à personalização.</span>
          </div>

          <!-- TEMA RÁPIDO -->
          <div class="csb-section" v-if="!isPremium && !customModeActive">
            <p class="csb-section__label">Tema rápido</p>
            <div class="csb-themes">
              <button
                v-for="t in THEMES"
                :key="t.key"
                class="csb-theme-btn"
                :class="{ 'csb-theme-btn--active': !customModeActive && localTheme === t.key }"
                @click="selectTheme(t.key)"
                :style="{ '--t-primary': t.primary, '--t-bg': t.bg }"
                :aria-pressed="!customModeActive && localTheme === t.key"
              >
                <span class="csb-theme-btn__swatch">
                  <span class="csb-theme-btn__primary"></span>
                  <span class="csb-theme-btn__bg"></span>
                </span>
                <span class="csb-theme-btn__label">{{ t.label }}</span>
                <i v-if="!customModeActive && localTheme === t.key" class="fas fa-check csb-theme-btn__check"></i>
              </button>
            </div>
            <div class="csb-save-status" :class="saveStatusClass">
              <template v-if="saving"><i class="fas fa-spinner fa-spin me-1"></i> Salvando…</template>
              <template v-else-if="saveError"><i class="fas fa-circle-exclamation me-1"></i> Erro ao salvar</template>
              <template v-else-if="saved"><i class="fas fa-check me-1"></i> Salvo!</template>
            </div>
          </div>

          <!-- Divider -->
          <div class="csb-divider" v-if="!isPremium"></div>

          <!-- CUSTOMIZAÇÃO PREMIUM -->
          <div class="csb-section">
            <!-- Header clicável com toggle -->
            <div class="csb-custom-header" v-if="!isPremium" @click="toggleCustomMode">
              <div>
                <div class="csb-premium__badge"><i class="fas fa-crown me-1"></i> Premium</div>
                <p class="csb-section__label mt-1 mb-0">Cores e fundo personalizados</p>
              </div>
              <label class="csb-toggle" @click.stop>
                <input type="checkbox" :checked="customModeActive" @change="toggleCustomMode" />
                <span class="csb-toggle__track"></span>
              </label>
            </div>

            <!-- Controles (modo custom ativo) -->
            <transition name="fade-up">
              <div v-if="customModeActive || isPremium" class="csb-custom-body">

                <div v-if="isPremium" class="csb-premium__active-info mt-2">
                  <i class="fas fa-circle-check me-1"></i> Premium ativo — configurações aplicadas.
                </div>

                <!-- Cor primária -->
                <div class="csb-field mt-3">
                  <label class="csb-field__label"><i class="fas fa-palette me-1"></i> Cor primária</label>
                  <div class="csb-color-row">
                    <input type="color" class="csb-color-picker" v-model="localColor" />
                    <span class="csb-color-hex">{{ localColor }}</span>
                    <div class="csb-color-preview" :style="{ background: localColor }"></div>
                  </div>
                </div>

                <!-- Imagem de fundo -->
                <div class="csb-field">
                  <label class="csb-field__label"><i class="fas fa-image me-1"></i> Imagem de fundo</label>
                  <div v-if="localBgImage || props.bgImageURL" class="csb-bg-preview">
                    <img :src="localBgImage || props.bgImageURL" class="csb-bg-preview__img" alt="Preview do fundo" />
                    <button class="csb-bg-preview__remove" @click="removeBgImage" title="Remover imagem">
                      <i class="fas fa-xmark"></i>
                    </button>
                  </div>

                  <div class="csb-bg-opacity">
                    <div class="csb-bg-opacity__header">
                      <span class="csb-bg-opacity__label"><i class="fas fa-circle-half-stroke me-1"></i> Opacidade da imagem</span>
                      <span class="csb-bg-opacity__value">{{ Math.round(localBgOpacity * 100) }}%</span>
                    </div>
                    <input
                      type="range"
                      min="0.05"
                      max="1"
                      step="0.01"
                      v-model.number="localBgOpacity"
                      class="csb-bg-opacity__slider"
                      :style="{
                        background: `linear-gradient(to right, var(--event-primary, #ff50a3) ${Math.round(localBgOpacity * 100)}%, #e5e7eb ${Math.round(localBgOpacity * 100)}%)`
                      }"
                    />
                    <div class="csb-bg-opacity__hints">
                      <span>- visível</span>
                      <span>+ visível</span>
                    </div>
                  </div>
                  <label class="csb-bg-upload">
                    <input type="file" accept="image/*" class="csb-bg-upload__input" @change="onBgFileChange" :disabled="premiumSaving" />
                    <i class="fas fa-arrow-up-from-bracket me-1"></i> {{ localBgImage ? 'Trocar imagem' : 'Escolher imagem' }}
                  </label>
                  <p class="csb-bg-upload__hint">JPG, PNG ou WebP · máx. 5 MB · use imagens claras para melhor leitura</p>
                </div>

                <!-- White label -->
                <div class="csb-field csb-field--toggle">
                  <div class="csb-field__info">
                    <label class="csb-field__label"><i class="fas fa-eye-slash me-1"></i> Remover marca QueMimo</label>
                    <span class="csb-field__hint">Seu evento, sua identidade</span>
                  </div>
                  <label class="csb-toggle">
                    <input type="checkbox" v-model="localWhiteLabel" :disabled="premiumSaving" />
                    <span class="csb-toggle__track"></span>
                  </label>
                </div>

                <!-- Salvar -->
                <button class="csb-premium__save-btn" :disabled="premiumSaving" @click="savePremiumSettings">
                  <template v-if="premiumSaving && bgUploading"><i class="fas fa-spinner fa-spin me-1"></i> Enviando imagem…</template>
                  <template v-else-if="premiumSaving"><i class="fas fa-spinner fa-spin me-1"></i> Salvando…</template>
                  <template v-else-if="premiumSaved"><i class="fas fa-check me-1" style="color:#16a34a"></i> Salvo!</template>
                  <template v-else><i class="fas fa-floppy-disk me-1"></i> {{ isPremium ? 'Atualizar' : 'Salvar preferências' }}</template>
                </button>

                <p v-if="premiumSaveError || bgUploadError" class="csb-premium__save-error">
                  <i class="fas fa-circle-exclamation me-1"></i> {{ bgUploadError || 'Erro ao salvar. Tente novamente.' }}
                </p>

                <!-- CTA Ativar -->
                <transition name="fade-up">
                  <button v-if="premiumSaved && !isPremium" class="csb-premium__cta" @click="$emit('request-activate-premium')">
                    <i class="fas fa-rocket me-1"></i> Ativar Premium &nbsp;·&nbsp; R$ 19,90
                  </button>
                </transition>
              </div>
            </transition>

            <!-- Dica quando desativado -->
            <p v-if="!customModeActive" class="csb-custom-hint">
              Personalize as cores e o fundo da sua página de presentes.
            </p>
          </div>
        </div>
      </div>
    </transition>


  </teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { patchCustomization, uploadCustomizationBackground } from '@/services/eventService';

const props = defineProps({
  modelValue:   { type: Boolean, default: false },
  eventId:      { type: String, required: true },
  activeTheme:  { type: String, default: 'quemimo' },
  saving:       { type: Boolean, default: false },
  saveError:    { type: Boolean, default: false },
  saved:        { type: Boolean, default: false },
  isPremium:    { type: Boolean, default: false },
  customColor:  { type: String, default: '#ff50a3' },
  whiteLabel:   { type: Boolean, default: false },
  bgImageURL:   { type: String, default: '' },
  bgOpacity:    { type: Number, default: 0.95 },
  croppedBg:    { type: Object, default: null }, // { blob, dataUrl } — vem do ImageCropperModal no pai
});

const emit = defineEmits(['update:modelValue', 'theme-changed', 'live-color-changed', 'live-bg-changed', 'live-bg-opacity-changed', 'request-bg-crop', 'request-activate-premium', 'premium-settings-saved', 'live-white-label-changed']);

const THEMES = [
  { key: 'quemimo', label: 'Rosa QueMimo',  primary: '#ff50a3', bg: '#fff5f9' },
  { key: 'bebe',    label: 'Azul Bebê',     primary: '#5b9bd5', bg: '#f0f6ff' },
  { key: 'neutro',  label: 'Cinza Neutro',  primary: '#6b7280', bg: '#f9fafb' },
];

const customModeActive = ref(props.activeTheme === 'premium');

const localTheme      = ref(props.activeTheme);
const lastQuickTheme  = ref(props.activeTheme !== 'premium' ? props.activeTheme : 'quemimo');
const localColor      = ref(props.customColor || '#ff50a3');
const localWhiteLabel = ref(props.whiteLabel);
const localBgImage   = ref(props.bgImageURL || '');
const localBgOpacity = ref(props.bgOpacity ?? 0.95); // opacidade da imagem: 1 = totalmente visível, 0 = invisível
const bgUploading    = ref(false);
const bgUploadError  = ref('');
const premiumSaving  = ref(false);
const premiumSaved   = ref(false);
const premiumSaveError = ref(false);

// Arquivo pendente: selecionado pelo usuário, enviado somente ao salvar
let pendingBgFile = null;

// Quando o pai confirma o crop, atualiza o preview e guarda o arquivo pendente
watch(() => props.croppedBg, (v) => {
  if (v) {
    localBgImage.value = v.dataUrl;
    pendingBgFile = new File([v.blob], 'background.jpg', { type: 'image/jpeg' });
  }
});

watch(() => props.activeTheme, (v) => {
  localTheme.value = v;
  customModeActive.value = v === 'premium';
  // Atualiza o último tema rápido quando o pai sincroniza o tema da API
  // (activeTheme começa null e é preenchido após a resposta da API)
  if (v && v !== 'premium') lastQuickTheme.value = v;
});
watch(() => props.isPremium, (v) => { if (v) { premiumSaved.value = false; customModeActive.value = true; } });
watch(() => props.customColor, (v) => { if (v) localColor.value = v; });
watch(() => props.whiteLabel, (v) => { localWhiteLabel.value = v; });
watch(localWhiteLabel, (v) => { emit('live-white-label-changed', v); });
watch(localBgOpacity, (v) => { emit('live-bg-opacity-changed', v); });
watch(() => props.bgOpacity, (v) => { if (v !== undefined) localBgOpacity.value = v; });
watch(() => props.bgImageURL, (v) => { localBgImage.value = v || ''; }, { immediate: true });

// Deriva as CSS vars de luz/sombra a partir de uma cor hex
const hexToVars = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return {
    light: `rgba(${r}, ${g}, ${b}, 0.08)`,
    shadow: `rgba(${r}, ${g}, ${b}, 0.35)`,
  };
};

// Live preview: ao mudar a cor, seleciona o tema Personalizado e aplica imediatamente
const applyLiveColor = (color) => {
  const { light, shadow } = hexToVars(color);
  document.documentElement.style.setProperty('--event-primary', color);
  document.documentElement.style.setProperty('--event-primary-light', light);
  document.documentElement.style.setProperty('--event-primary-shadow', shadow);
  emit('live-color-changed', color);
};

watch(localColor, (color) => {
  if (customModeActive.value) applyLiveColor(color);
});

const onBgFileChange = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;
  bgUploadError.value = '';
  // Lê o arquivo e pede ao pai para abrir o cropper
  const reader = new FileReader();
  reader.onload = (ev) => {
    emit('request-bg-crop', ev.target.result);
  };
  reader.readAsDataURL(file);
  e.target.value = '';
};

const removeBgImage = () => {
  localBgImage.value = '';
  pendingBgFile = null;
  emit('live-bg-changed', '');
};

// Reset ao abrir
watch(() => props.modelValue, (open) => {
  if (open) {
    premiumSaved.value = false;
    premiumSaveError.value = false;
  }
});

const saveStatusClass = computed(() => ({
  'csb-save-status--saving': props.saving,
  'csb-save-status--error':  props.saveError,
  'csb-save-status--saved':  props.saved,
}));

const selectTheme = (key) => {
  const wasPremium = customModeActive.value;
  customModeActive.value = false;
  localTheme.value = key;
  lastQuickTheme.value = key;
  const theme = THEMES.find(t => t.key === key);
  if (theme) applyLiveColor(theme.primary);
  // Limpa o fundo personalizado na página apenas quando saindo do modo premium
  if (wasPremium) emit('live-bg-changed', '');
  emit('theme-changed', key);
};

const enableCustomMode = () => {
  // Usa a cor salva/atual se disponível; só faz seed pelo tema base se não há cor definida.
  if (!localColor.value || !customModeActive.value) {
    const baseTheme = THEMES.find(t => t.key === lastQuickTheme.value);
    if (baseTheme && !props.customColor) localColor.value = baseTheme.primary;
  }
  customModeActive.value = true;
  localTheme.value = 'premium';
  applyLiveColor(localColor.value);
  // Restaura o fundo personalizado caso já exista
  if (localBgImage.value) emit('live-bg-changed', localBgImage.value);
  emit('theme-changed', 'premium');
};

const toggleCustomMode = () => {
  if (customModeActive.value) selectTheme(lastQuickTheme.value);
  else enableCustomMode();
};

const savePremiumSettings = async () => {
  bgUploadError.value = '';
  premiumSaving.value = true;
  premiumSaved.value  = false;
  premiumSaveError.value = false;

  // Faz upload do arquivo pendente se houver
  let finalBgUrl = localBgImage.value;
  if (pendingBgFile) {
    bgUploading.value = true;
    try {
      const res = await uploadCustomizationBackground(props.eventId, pendingBgFile);
      finalBgUrl = res.data?.url || '';
      localBgImage.value = finalBgUrl;
      emit('live-bg-changed', finalBgUrl);
      pendingBgFile = null;
    } catch {
      bgUploadError.value = 'Erro ao enviar imagem. Tente novamente.';
      premiumSaving.value = false;
      bgUploading.value = false;
      return;
    } finally {
      bgUploading.value = false;
    }
  }

  // Se ainda não é premium, apenas armazena localmente e exibe o CTA de ativação
  if (!props.isPremium) {
    premiumSaving.value = false;
    premiumSaved.value = true;
    emit('premium-settings-saved', { color: localColor.value, whiteLabel: localWhiteLabel.value, bgImage: finalBgUrl });
    return;
  }

  try {
    await patchCustomization(props.eventId, {
      theme:                'premium',
      primary_color:        localColor.value,
      white_label:          localWhiteLabel.value,
      background_opacity:   localBgOpacity.value,
      background_image_url: finalBgUrl || '',
    });
    localTheme.value = 'premium';
    premiumSaved.value = true;
    setTimeout(() => { premiumSaved.value = false; }, 2500);
    emit('premium-settings-saved', { color: localColor.value, whiteLabel: localWhiteLabel.value, bgImage: finalBgUrl });
  } catch {
    premiumSaveError.value = true;
  } finally {
    premiumSaving.value = false;
  }
};
</script>

<style scoped>
/* ── Backdrop ──────────────────────────────── */
.csb-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 12, 24, 0.58);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 10050;
}

/* ── Painel ────────────────────────────────── */
.csb-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 324px;
  max-width: 90vw;
  background: linear-gradient(180deg, #ffffff 0%, #fbfcff 48%, #f8fafc 100%);
  z-index: 10051;
  display: flex;
  flex-direction: column;
  box-shadow: -18px 0 48px rgba(15, 23, 42, 0.18);
  border-left: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 16px 0 0 16px;
}

/* ── Header ────────────────────────────────── */
.csb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.05rem 1.15rem 0.95rem;
  background: rgba(255, 255, 255, 0.86);
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 1px 0 rgba(255,255,255,0.8) inset;
}

.csb-header__info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.csb-header__icon {
  color: var(--event-primary, #ff50a3);
  font-size: 0.95rem;
  filter: drop-shadow(0 2px 6px color-mix(in srgb, var(--event-primary, #ff50a3) 30%, transparent));
}
.csb-header__title { font-size: 1rem; font-weight: 750; color: #26314f; }

.csb-close {
  border: none;
  background: #f1f5f9;
  color: #64748b;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, transform 0.15s;
}
.csb-close:hover { background: #e2e8f0; color: #334155; transform: rotate(8deg); }

/* ── Body ──────────────────────────────────── */
.csb-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.15rem 1.15rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.35) transparent;
}
.csb-body::-webkit-scrollbar { width: 4px; }
.csb-body::-webkit-scrollbar-thumb { background: rgba(148, 163, 184, 0.35); border-radius: 999px; }

/* ── Aviso ─────────────────────────────────── */
.csb-owner-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
  margin-bottom: 0.85rem;
  font-size: 0.72rem;
  color: #64748b;
  line-height: 1.4;
}
.csb-owner-notice__icon { flex-shrink: 0; margin-top: 1px; font-size: 0.7rem; color: #94a3b8; }

/* ── Seção ─────────────────────────────────── */
.csb-section__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  color: #9a9db5;
  margin: 0 0 0.75rem;
}

/* ── Temas ─────────────────────────────────── */
.csb-themes { display: flex; flex-direction: column; gap: 0.5rem; }

.csb-theme-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.6rem 0.75rem;
  background: #f9f9fc;
  border: 2px solid transparent;
  border-radius: 0.75rem;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.18s, background 0.18s;
  position: relative;
}
.csb-theme-btn:hover { background: #f3f3f9; }
.csb-theme-btn--active { border-color: var(--t-primary); background: #fff; }

.csb-theme-btn__swatch {
  flex-shrink: 0;
  display: flex;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}
.csb-theme-btn__primary { flex: 1; background: var(--t-primary); }
.csb-theme-btn__bg      { flex: 1; background: var(--t-bg); }
.csb-theme-btn__label   { font-size: 0.88rem; font-weight: 600; color: #32355d; flex: 1; }
.csb-theme-btn__check   { font-size: 0.75rem; color: var(--t-primary); }

/* ── Seção faded (preset quando custom mode ativo) ──────────── */
.csb-section--faded { opacity: 0.42; pointer-events: none; transition: opacity 0.25s; }

/* ── Custom mode header ─────────────────────────────────────── */
.csb-custom-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.45rem 0.6rem;
  border-radius: 0.65rem;
  transition: background 0.15s;
  margin-bottom: 0.1rem;
}
.csb-custom-header:hover { background: #f9f9fc; }

.csb-custom-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 0.25rem;
}

.csb-custom-hint {
  font-size: 0.78rem;
  color: #9ca3af;
  margin: 0.4rem 0.2rem 0;
  line-height: 1.45;
}

/* ── Status salvamento ──────────────────────── */
.csb-save-status { margin-top: 0.75rem; font-size: 0.78rem; color: #9a9db5; min-height: 1.2rem; transition: color 0.2s; }
.csb-save-status--saving { color: #9a9db5; }
.csb-save-status--error  { color: #ef4444; }
.csb-save-status--saved  { color: #22c55e; }

/* ── Divisor ────────────────────────────────── */
.csb-divider { height: 1px; background: #eef2f7; margin: 1rem 0; }

/* ── Premium ────────────────────────────────── */
.csb-premium__header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.25rem;
}

.csb-premium__badge {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
}

.csb-premium__active-tag {
  font-size: 0.72rem;
  font-weight: 700;
  color: #22c55e;
}

/* ── Campo genérico ─────────────────────────── */
.csb-field {
  margin-bottom: 0;
  padding: 0.72rem;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #e9eef5;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}
.csb-field__label { font-size: 0.82rem; font-weight: 700; color: #34405f; display: block; margin-bottom: 0.45rem; }
.csb-field__hint  { font-size: 0.72rem; color: #94a3b8; }

/* ── Cor ────────────────────────────────────── */
.csb-color-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.65rem;
}

.csb-color-picker {
  width: 42px;
  height: 42px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  padding: 2px;
  background: #fff;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.08);
}
.csb-color-picker::-webkit-color-swatch-wrapper { padding: 0; border-radius: 0.4rem; }
.csb-color-picker::-webkit-color-swatch { border: none; border-radius: 0.4rem; }

.csb-color-hex {
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
  font-family: monospace;
  flex: 1;
}

.csb-color-preview {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid rgba(0,0,0,0.08);
  flex-shrink: 0;
  transition: background 0.2s;
}

/* ── Toggle ─────────────────────────────────── */
.csb-field--toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.csb-field--toggle .csb-field__info { flex: 1; }

.csb-toggle { position: relative; display: inline-flex; align-items: center; cursor: pointer; }
.csb-toggle input { position: absolute; opacity: 0; width: 0; height: 0; }

.csb-toggle__track {
  display: block;
  width: 40px;
  height: 22px;
  background: #e5e7eb;
  border-radius: 999px;
  transition: background 0.2s;
  position: relative;
}
.csb-toggle__track::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
}
.csb-toggle input:checked ~ .csb-toggle__track { background: var(--event-primary, #ff50a3); }
.csb-toggle input:checked ~ .csb-toggle__track::after { transform: translateX(18px); }
.csb-toggle--disabled { opacity: 0.5; pointer-events: none; }

/* ── Botão Salvar Premium ───────────────────── */
.csb-premium__save-btn {
  width: 100%;
  padding: 0.68rem;
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  margin-bottom: 0;
  transition: border-color 0.18s, background 0.18s, transform 0.15s, box-shadow 0.18s;
}
.csb-premium__save-btn:hover:not(:disabled) {
  border-color: color-mix(in srgb, var(--event-primary, #ff50a3) 55%, #dbe4ef);
  background: color-mix(in srgb, var(--event-primary, #ff50a3) 7%, #fff);
  box-shadow: 0 8px 20px color-mix(in srgb, var(--event-primary, #ff50a3) 12%, transparent);
  transform: translateY(-1px);
}
.csb-premium__save-btn:disabled { opacity: 0.65; cursor: not-allowed; }

.csb-premium__save-error { font-size: 0.78rem; color: #ef4444; margin-top: -0.5rem; margin-bottom: 0.5rem; }

/* ── CTA Ativar ─────────────────────────────── */
.csb-premium__cta {
  width: 100%;
  padding: 0.65rem;
  border: none;
  border-radius: 0.65rem;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #fff;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  transition: opacity 0.18s, transform 0.18s;
  box-shadow: 0 3px 12px rgba(217, 119, 6, 0.35);
}
.csb-premium__cta:hover { opacity: 0.92; transform: translateY(-1px); }

/* ── Estado ativo ───────────────────────────── */
.csb-premium__active-info {
  font-size: 0.78rem;
  color: #15803d;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 0.68rem 0.78rem;
  margin-top: 0;
  box-shadow: 0 6px 18px rgba(34, 197, 94, 0.08);
}

.csb-bg-preview {
  position: relative;
  width: 100%;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 0.65rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
}
.csb-bg-preview__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.csb-bg-preview__remove {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0,0,0,0.55);
  color: #fff;
  border: none;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
}
.csb-bg-preview__remove:hover { background: rgba(0,0,0,0.8); }

.csb-bg-opacity {
  margin-bottom: 0.7rem;
  padding: 0.65rem;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 8px;
}
.csb-bg-opacity__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}
.csb-bg-opacity__label {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
}
.csb-bg-opacity__value {
  font-size: 0.75rem;
  color: var(--event-primary, #ff50a3);
  font-weight: 700;
  min-width: 36px;
  text-align: right;
}
.csb-bg-opacity__slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 5px;
  border-radius: 999px;
  background: #e5e7eb;
  outline: none;
  cursor: pointer;
}
.csb-bg-opacity__slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: var(--event-primary, #ff50a3);
  cursor: pointer;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.22);
}
.csb-bg-opacity__slider::-moz-range-thumb {
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: var(--event-primary, #ff50a3);
  border: 3px solid #fff;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.22);
}
.csb-bg-opacity__hints {
  display: flex;
  justify-content: space-between;
  font-size: 0.65rem;
  color: #adb5bd;
  margin-top: 0.2rem;
}

.csb-bg-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.62rem;
  border: 1.5px dashed #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.18s, background 0.18s;
}
.csb-bg-upload:hover:not(.csb-bg-upload--loading) {
  border-color: color-mix(in srgb, var(--event-primary, #ff50a3) 55%, #cbd5e1);
  background: color-mix(in srgb, var(--event-primary, #ff50a3) 7%, #fff);
  color: var(--event-primary, #ff50a3);
}
.csb-bg-upload--loading { opacity: 0.7; cursor: not-allowed; }
.csb-bg-upload__input { display: none; }
.csb-bg-upload__error { font-size: 0.75rem; color: #ef4444; margin-top: 0.3rem; margin-bottom: 0; }
.csb-bg-upload__hint  { font-size: 0.7rem; color: #9ca3af; margin-top: 0.3rem; margin-bottom: 0; }

/* ── Transições ─────────────────────────────── */
.fade-enter-active, .fade-leave-active   { transition: opacity 0.22s ease; }
.fade-enter-from, .fade-leave-to         { opacity: 0; }

.slide-enter-active, .slide-leave-active { transition: transform 0.28s cubic-bezier(.4,0,.2,1); }
.slide-enter-from, .slide-leave-to       { transform: translateX(100%); }

.fade-up-enter-active { transition: opacity 0.3s, transform 0.3s; }
.fade-up-enter-from   { opacity: 0; transform: translateY(8px); }
.fade-up-leave-active { transition: opacity 0.2s; }
.fade-up-leave-to     { opacity: 0; }
</style>
